import { LoadingButton } from "@mui/lab";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { APIMonitoringEnrollment } from "../../../types/APIMonitoringEnrollment";
import { useTrueBizApi } from "../../../api";
import { useShowNetworkFailureToast } from "../../NetworkFailureToast";
import { useCloseIssueDetailModal } from "../MonitorDetailModal/hooks";

export default function UnEnrollButton({ domain }: { domain: string }) {
  const queryClient = useQueryClient();
  const api = useTrueBizApi();
  const closeModal = useCloseIssueDetailModal();
  const showToast = useShowNetworkFailureToast();

  const deleteMutation = useMutation({
    mutationFn: async ({ domain }: { domain: string }) => {
      await api.unEnrollMonitoringDomain({ domain });
    },
    mutationKey: ["monitors", "delete"],
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ["allMonitors"] });
      const previous = queryClient.getQueryData(["allMonitors"]);

      if (previous === undefined) return; // query hasn't been populated yet

      queryClient.setQueryData(["allMonitors"], (old: any) => {
        const updated = {
          ...old,
          monitors: old.monitors.filter(
            (monitor: APIMonitoringEnrollment) =>
              monitor.domain !== variables.domain
          ),
        };

        return updated;
      });

      return { previous };
    },
    onSuccess: async (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["allMonitors"],
        refetchType: "all",
      });

      queryClient.invalidateQueries({
        queryKey: ["getMonitors"],
        refetchType: "all",
      });

      await queryClient.invalidateQueries({
        queryKey: ["monitor portfolio"],
        refetchType: "all",
      });

      closeModal();
    },
    onError: (err, variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["allMonitors"], context.previous);
      }
      showToast();
    },
  });

  const [confirmed, setConfirmed] = useState(false);
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!active) return;
    if (confirmed) return;

    const reset = setTimeout(() => {
      setActive(false);
      setConfirmed(false);
    }, 5_000);

    return () => clearTimeout(reset);
  }, [active, confirmed]);

  return (
    <LoadingButton
      sx={{ height: 25, width: 130 }}
      onClick={() => {
        setActive(true);
        if (active) {
          setConfirmed(true);
          deleteMutation.mutate({ domain });
        }
      }}
      loading={confirmed}
      color={active ? "error" : undefined}
      variant={active ? "contained" : "outlined"}
      size="small"
    >
      {!active
        ? `Delete domain`
        : !confirmed
        ? `Confirm delete`
        : `Deleting ${domain}`}
    </LoadingButton>
  );
}
